import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useContext,
  useRef,
} from "react"
import {
  roundCurrency,
  toCents,
  fromCents,
  strictRound2,
  isNearZero,
} from "./utils/costsUtils"
import i18n from "i18n"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material"
import {
  useSaveExpenses,
  useAllocateExpense,
} from "../hooks/useOrganizationExpenses"
import ContactContext from "context/contact/contactContext"
import { useContactsLight } from "../../Contacts/hooks/useContacts"
import { useAuthContext } from "context/auth/authContext"
import CloseIcon from "@mui/icons-material/Close"
import PropTypes from "prop-types"
import CostItem from "./CostItem"
import CostsInputationGrid from "./components/CostsInputationGrid"

const EditCost = ({
  open,
  onClose,
  onSave,
  costData,
  projectsHeaders = [],
}) => {
  const { user } = useAuthContext()
  const organizationId = user && user["custom:organizationId"]
  const saveExpensesMutation = useSaveExpenses()
  const [tabValue, setTabValue] = useState(0)
  const [hasUserChangedTab, setHasUserChangedTab] = useState(false)
  const [remainingAmount, setRemainingAmount] = useState(0)
  const [allocation, setAllocation] = useState({})
  const allocateExpenseMutation = useAllocateExpense()
  const [formData, setFormData] = useState({
    expenseId: "",
    organizationId: "",
    id_external: "",
    date: "",
    title: "",
    projects: [],
    createdByProject: "",
    element: "",
    subtotal: 0,
    tax: [],
    retention: [],
    total: 0,
    allocated: 0,
    expense_type: "material",
    is_direct: true,
    providerId: "",
    provider: "",
    documents: [],
    notes: "",
  })
  const { Contacts } = useContext(ContactContext)
  const { data: contactsLight = [] } = useContactsLight(
    organizationId,
    !!organizationId,
  )

  useLayoutEffect(() => {
    if (!costData) return

    const projectIdsFromPartidas = Array.isArray(costData.partidas)
      ? Array.from(
          new Set(costData.partidas.map(p => p?.projectId).filter(Boolean)),
        )
      : []

    let newFormData = { ...costData, remainingAmount: costData.total || 0 }

    if (!newFormData.organizationId) {
      newFormData.organizationId = organizationId || ""
    }
    if (
      !Array.isArray(newFormData.projects) ||
      newFormData.projects.length === 0
    ) {
      newFormData.projects = projectIdsFromPartidas
    }
    if (
      (!costData.providerId || costData.providerId === "") &&
      costData.provider &&
      Contacts &&
      Contacts.length > 0
    ) {
      const found = Contacts.find(c => c.completeName === costData.provider)
      if (found) {
        newFormData.providerId = found.id
      }
    }

    setFormData(newFormData)
    const remInit = strictRound2(costData.total || 0)
    setRemainingAmount(remInit)
  }, [costData, Contacts, organizationId])

  const prevExpenseIdRef = useRef()
  useEffect(() => {
    const currentId = costData?.expenseId
    if (currentId && prevExpenseIdRef.current !== currentId) {
      if (!hasUserChangedTab) {
        setTabValue(0)
      }
      setHasUserChangedTab(false)
      prevExpenseIdRef.current = currentId
    }
  }, [costData?.expenseId, hasUserChangedTab])

  const handleInputChange = field => event => {
    let value = event.target.value
    if (["subtotal", "total"].includes(field)) {
      value = value === "" ? 0 : Number(value)
    }
    setFormData(prev => {
      const updatedData = {
        ...prev,
        [field]: value,
      }
      if (field === "total") {
        const fixed = strictRound2(value)
        updatedData.total = fixed
        updatedData.remainingAmount = fixed
        setRemainingAmount(fixed)
      }
      return updatedData
    })
  }

  const handleRemainingAmountChange = newRemainingAmount => {
    setRemainingAmount(newRemainingAmount)
  }

  const handleTabChange = (event, newValue) => {
    setHasUserChangedTab(true)
    setTabValue(newValue)
  }

  const [saveDone, setSaveDone] = useState(false)
  const [allocateDone, setAllocateDone] = useState(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const handleSave = () => {
    setSaveDone(false)
    setAllocateDone(false)
    allocateExpense()
    saveEditedCost()
  }

  const allocateExpense = () => {
    const partidas = Object.entries(allocation)
      .filter(([id]) => !id.endsWith("_c") && id !== "organization")
      .map(([partidaId, data]) => {
        const decomp = Array.isArray(data.decomposition)
          ? data.decomposition
          : []
        return {
          partidaId,
          projectId: data.projectId || undefined,
          allocated: fromCents(toCents(data.allocated || 0)),
          decompositions: decomp.map(v => fromCents(toCents(v ?? 0))),
        }
      })
      .filter(p => p.allocated > 0)

    const totalAllocated = fromCents(
      Object.entries(allocation)
        .filter(([id]) => !id.endsWith("_c"))
        .reduce((sum, [id, data]) => sum + toCents(data?.allocated || 0), 0),
    )
    const allocationBody = {
      organizationId: formData.organizationId || organizationId || "",
      expenseId: formData.expenseId || "",
      allocated: totalAllocated,
      partidas,
    }

    allocateExpenseMutation.mutate(
      { imputations: allocationBody },
      {
        onSuccess: () => {
          if (isMountedRef.current) setAllocateDone(true)
        },
      },
    )
  }
  const saveEditedCost = () => {
    if (!organizationId) return

    const allocationValues = Object.values(allocation || {})
    const projectIds = [
      ...new Set(
        allocationValues
          .filter(a => a?.projectId && a.allocated > 0)
          .map(a => a.projectId),
      ),
    ]

    const totalAllocated = fromCents(
      Object.entries(allocation || {})
        .filter(([id]) => !id.endsWith("_c"))
        .reduce((sum, [id, data]) => sum + toCents(data?.allocated || 0), 0),
    )

    const totalValue = fromCents(toCents(Number(formData.total) || 0))
    let remaining = fromCents(toCents(totalValue) - toCents(totalAllocated))
    if (isNearZero(remaining)) remaining = 0
    setRemainingAmount(remaining)

    const dataToSave = {
      ...formData,
      organizationId,
      projectId: projectIds,
      allocated: totalAllocated,
      remainingAmount: remaining,
    }

    delete dataToSave.projects
    saveExpensesMutation.mutate(
      { expenses: [dataToSave] },
      {
        onSuccess: () => {
          onSave?.(dataToSave)
          if (isMountedRef.current) setSaveDone(true)
        },
      },
    )
  }

  useEffect(() => {
    if (saveDone && allocateDone) {
      onClose()
      saveExpensesMutation.reset()
      allocateExpenseMutation.reset()
    }
  }, [
    saveDone,
    allocateDone,
    onClose,
    saveExpensesMutation,
    allocateExpenseMutation,
  ])

  const handleClose = () => {
    onClose()
  }

  const isFormValid = () => {
    const basicValidation =
      formData.date &&
      formData.title &&
      formData.expense_type &&
      typeof formData.is_direct === "boolean" &&
      typeof formData.subtotal === "number" &&
      formData.subtotal >= 0 &&
      Array.isArray(formData.tax) &&
      Array.isArray(formData.retention)

    const remainingNormalized = isNearZero(remainingAmount)
      ? 0
      : strictRound2(remainingAmount)
    const remainingAmountValid = remainingNormalized >= 0

    const totalAllocated = fromCents(
      Object.entries(allocation || {})
        .filter(([id]) => !id.endsWith("_c"))
        .reduce((sum, [id, data]) => sum + toCents(data?.allocated || 0), 0),
    )
    const totalValue = fromCents(toCents(Number(formData.total) || 0))
    const allocatedNotExceedTotal =
      toCents(totalAllocated) <= toCents(totalValue)

    return basicValidation && remainingAmountValid && allocatedNotExceedTotal
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {i18n.t("costs.modal.editCost")}
        <IconButton
          aria-label={i18n.t("costs.modal.close")}
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ width: "100%" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label={i18n.t("costs.modal.generalData")} />
              <Tab label={i18n.t("costs.modal.costsInputation")} />
            </Tabs>
          </Box>

          <Box sx={{ mt: 2, display: tabValue === 0 ? "block" : "none" }}>
            <CostItem
              formData={formData}
              handleInputChange={handleInputChange}
              readOnlyAmounts={true}
              contactsLight={contactsLight}
            />
          </Box>
          <Box sx={{ mt: 2, display: tabValue === 1 ? "block" : "none" }}>
            <CostsInputationGrid
              partidas={costData.partidas || []}
              formData={formData}
              handleInputChange={handleInputChange}
              onRemainingAmountChange={handleRemainingAmountChange}
              projectsHeaders={projectsHeaders}
              allocation={allocation}
              setAllocation={setAllocation}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant="outlined" onClick={handleClose}>
          {i18n.t("costs.modal.cancel")}
        </Button>
        <Button
          variant="contained"
          color="branded"
          onClick={handleSave}
          sx={{ width: "120px" }}
          disabled={!isFormValid() || saveExpensesMutation.isLoading}
        >
          {saveExpensesMutation.isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            i18n.t("costs.modal.save")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

EditCost.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  costData: PropTypes.object,
  projectsHeaders: PropTypes.array,
}

export default EditCost
